import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, ShieldAlert, MapPin, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const SEV = {
  green:  { pill: "bg-emerald-50 border-emerald-200 text-emerald-800",  label: "On track"  },
  orange: { pill: "bg-amber-50 border-amber-200 text-amber-800",        label: "2 days"    },
  red:    { pill: "bg-rose-50 border-rose-200 text-rose-800",           label: "Priority"  },
  breach: { pill: "bg-red-100 border-red-400 text-red-900 font-bold",   label: "SOP BREACH" },
  unknown:{ pill: "bg-slate-50 border-slate-200 text-slate-700",        label: "No date"   },
};

const daysLabel = (n) => {
  if (n === null || n === undefined) return "—";
  if (n < 0) return `${Math.abs(n)}d overdue`;
  if (n === 0) return "arriving today";
  if (n === 1) return "1 day left";
  return `${n} days left`;
};

/**
 * Compliance Alerts — surfaces P0 tasks that carry SOP consequences:
 *   • PCWM 3-day placement countdown (green → orange → red → breach)
 *   • C-Form 24-hour Immigration Policy breach (with Founder/BDOM dismiss)
 *
 * Renders on the Dashboard. Empty state hides the entire card.
 */
export default function ComplianceAlerts() {
  const { user } = useAuth();
  const role = user?.effective_role;
  const canDismiss = role === "FOUNDER" || role === "BDOM";
  const [data, setData] = useState({ placement_alerts: [], cform_alerts: [] });
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/compliance/alerts");
      setData(data);
    } catch (e) {
      console.debug("compliance alerts:", e?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Pre-compute the human "Jaipur, India" location label once per data
  // refresh — cheaper than filter+join inside the render loop.
  const decoratedPlacements = useMemo(
    () => data.placement_alerts.map((a) => ({
      ...a,
      locationLabel: [a.city, a.destination].filter(Boolean).join(", "),
    })),
    [data.placement_alerts],
  );
  const decoratedCforms = useMemo(
    () => data.cform_alerts.map((a) => ({
      ...a,
      locationLabel: [a.city, a.destination].filter(Boolean).join(", "),
    })),
    [data.cform_alerts],
  );

  useEffect(() => {
    load();
    const t = setInterval(load, 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const dismiss = async () => {
    if (!dismissing) return;
    try {
      await api.post(`/compliance/cform-alert/${dismissing.id}/dismiss`, { reason });
      toast.success("SOP breach warning cleared");
      setDismissing(null);
      setReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to dismiss");
    }
  };

  const totalAlerts = decoratedPlacements.length + decoratedCforms.length;
  if (loading) return null;
  if (totalAlerts === 0) return null;

  return (
    <div className="aii-card p-5 border-l-4 border-rose-500" data-testid="compliance-alerts">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-rose-700 font-bold flex items-center gap-1">
            <ShieldAlert size={11} /> Compliance alerts
          </div>
          <h3 className="font-display font-semibold text-lg mt-0.5">
            {totalAlerts} action{totalAlerts === 1 ? "" : "s"} needed
          </h3>
        </div>
        <span className="text-[11px] text-[color:var(--aii-muted)]">
          Refreshes every 60s. Auto-clears when the placement or C-Form is filed.
        </span>
      </div>

      {data.placement_alerts.length > 0 && (
        <div className="mb-4">
          <div className="text-xs uppercase tracking-wider font-semibold text-[color:var(--aii-text-2)] mb-2 flex items-center gap-1">
            <Clock size={11} /> Placement countdown ({data.placement_alerts.length})
          </div>
          <div className="space-y-2">
            {decoratedPlacements.slice(0, 8).map((a) => {
              const sev = SEV[a.severity] || SEV.unknown;
              return (
                <Link
                  key={a.id}
                  to="/program-bookings"
                  data-testid={`placement-alert-${a.id}`}
                  className={`flex items-center justify-between gap-3 border rounded-lg p-3 text-sm hover:shadow-sm transition ${sev.pill}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{a.participant}</div>
                    <div className="text-xs opacity-90 flex items-center gap-1 flex-wrap">
                      <span>{a.programme || "Program"}</span>
                      {a.locationLabel && (
                        <span className="flex items-center gap-0.5"><MapPin size={9} /> {a.locationLabel}</span>
                      )}
                      {a.partner && <span className="opacity-70">· {a.partner}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-widest opacity-80">{sev.label}</div>
                    <div className="font-semibold text-sm">{daysLabel(a.days_left)}</div>
                    <div className="text-[10px] opacity-70">{a.start_date}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {decoratedCforms.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-red-900 mb-2 flex items-center gap-1">
            <AlertTriangle size={11} /> SOP Immigration Policy — C-Form breach ({decoratedCforms.length})
          </div>
          <div className="space-y-2">
            {decoratedCforms.slice(0, 8).map((a) => (
              <div
                key={a.id}
                data-testid={`cform-alert-${a.id}`}
                className="flex items-center justify-between gap-3 border rounded-lg p-3 text-sm bg-red-50 border-red-300"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-red-900 truncate">{a.participant}</div>
                  <div className="text-xs text-red-800 flex items-center gap-1 flex-wrap">
                    <span>Arrived {a.arrival_date}</span>
                    <span className="flex items-center gap-0.5"><MapPin size={9} /> {a.locationLabel}</span>
                    {a.partner && <span className="opacity-70">· {a.partner}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-widest text-red-800 font-bold">SOP Breach</div>
                  <div className="font-semibold text-sm text-red-900">{a.hours_overdue}h overdue</div>
                </div>
                {canDismiss ? (
                  <button
                    data-testid={`cform-dismiss-${a.id}`}
                    onClick={() => { setDismissing(a); setReason(""); }}
                    className="text-[11px] px-2 py-1 bg-white border border-red-400 text-red-800 rounded hover:bg-red-100 flex items-center gap-1 shrink-0"
                  >
                    <X size={11} /> Stop
                  </button>
                ) : (
                  <Link to="/cforms" className="text-[11px] px-2 py-1 bg-red-700 text-white rounded flex items-center gap-1 shrink-0">
                    <CheckCircle2 size={11} /> Fix
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {dismissing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="cform-dismiss-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Stop SOP breach warning</h4>
              <button onClick={() => setDismissing(null)}><X size={16} /></button>
            </div>
            <p className="text-sm text-[color:var(--aii-text-2)]">
              Silencing the warning for <b>{dismissing.participant}</b>. Please share a reason for the audit log — this stays permanent.
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Participant left early / diplomatic visa exemption / GEHM confirmed off-record."
              rows={3}
              className="w-full px-3 py-2 border border-[color:var(--aii-border)] rounded text-sm"
              data-testid="cform-dismiss-reason"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setDismissing(null)} className="text-sm px-3 py-2 border border-[color:var(--aii-border)] rounded">Cancel</button>
              <button
                data-testid="cform-dismiss-confirm"
                onClick={dismiss}
                disabled={!reason.trim()}
                className="text-sm px-3 py-2 bg-red-700 text-white rounded hover:bg-red-800 disabled:opacity-50"
              >
                Stop warning
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
